import { useState, useMemo } from "react";
import { CHARITIES, COMPANY } from "../lib/content";
import { Reveal, MaskReveal } from "../components/Reveal";
import { ArrowUpRight, Heart } from "lucide-react";

// Theme taxonomy
const THEMES = [
  { key: "all",       label: "All Partners" },
  { key: "health",    label: "Health & Healing" },
  { key: "women",     label: "Women & Girls" },
  { key: "youth",     label: "Children & Youth" },
  { key: "planet",    label: "Planet & Animals" },
  { key: "relief",    label: "Hunger & Relief" },
  { key: "education", label: "Inclusion & Education" },
];

// ─── Single editorial row — like the contents page of a magazine ───
function HonorRollEntry({ c, n }) {
  const logo = c.logoSrc || `https://logo.clearbit.com/${c.domain}?size=256`;
  const fallback = `https://www.google.com/s2/favicons?domain=${c.domain}&sz=256`;
  return (
    <a
      href={c.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative block border-t border-ink/15 hover:border-ink transition-colors duration-500"
      data-testid={`charity-${c.domain.replace(/\./g, "-")}`}
    >
      {/* Left-edge accent that draws in on hover */}
      <span className="absolute left-0 top-0 h-full w-[3px] bg-ink scale-y-0 origin-top group-hover:scale-y-100 transition-transform duration-500" aria-hidden />

      <div className="grid grid-cols-12 gap-x-6 gap-y-5 items-center py-8 md:py-10 pr-2 md:pr-4 pl-4 md:pl-8 group-hover:pl-6 md:group-hover:pl-12 transition-[padding] duration-500">
        {/* Number */}
        <div className="col-span-3 md:col-span-2">
          <span className="font-display text-4xl md:text-5xl text-ink/35 group-hover:text-ink transition-colors duration-500 tabular-nums tracking-tight">
            {String(n).padStart(2, "0")}
          </span>
        </div>

        {/* Logo mark — grayscale until hover */}
        <div className="col-span-9 md:col-span-2 flex items-center justify-start md:justify-center">
          <div className="h-16 md:h-20 w-full flex items-center justify-start md:justify-center">
            <img
              src={logo}
              alt={`${c.name} logo`}
              onError={(e) => { if (e.currentTarget.src !== fallback) e.currentTarget.src = fallback; }}
              style={{ maxHeight: "76px", maxWidth: "90%" }}
              className="w-auto h-auto object-contain select-none grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700"
              loading="lazy"
              draggable="false"
            />
          </div>
        </div>

        {/* Name + blurb */}
        <div className="col-span-12 md:col-span-5">
          <div className="overline text-ink/55 mb-2">{c.cause}</div>
          <h3 className="font-serif text-2xl md:text-[2rem] leading-[1.1] text-ink">
            {c.name}
          </h3>
          <p className="text-ink/70 mt-3 leading-relaxed text-[15px] md:text-base max-w-2xl">
            {c.blurb}
          </p>
        </div>

        {/* CTA + domain */}
        <div className="col-span-12 md:col-span-3 md:text-right md:self-center">
          <span className="inline-flex items-center gap-2 overline text-ink group-hover:gap-3 transition-all duration-300">
            Donate · Follow · Show up
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
          <div className="text-xs text-ink/45 mt-3 font-light tracking-wide">
            {c.domain}
          </div>
        </div>
      </div>
    </a>
  );
}

export default function Giveback() {
  const [activeTheme, setActiveTheme] = useState("all");

  const filtered = useMemo(
    () => (activeTheme === "all" ? CHARITIES : CHARITIES.filter((c) => c.theme === activeTheme)),
    [activeTheme]
  );

  const counts = useMemo(() => {
    const out = { all: CHARITIES.length };
    for (const c of CHARITIES) out[c.theme] = (out[c.theme] || 0) + 1;
    return out;
  }, []);

  return (
    <div className="pt-32 md:pt-40 pb-24 bg-cream" data-testid="giveback-page">
      {/* ───── HERO ───── */}
      <section className="container-edge">
        <div className="overline text-ink/60">The House · Giveback</div>
        <MaskReveal delay={0.05}>
          <h1 className="font-display uppercase text-7xl md:text-[12vw] leading-[0.82] mt-4">
            Ten percent <br/>
            <span className="serif-italic font-serif normal-case">returns to the room</span>.
          </h1>
        </MaskReveal>

        <div className="grid grid-cols-12 gap-8 mt-12 md:mt-16">
          <Reveal delay={0.2} className="col-span-12 md:col-span-7">
            <p className="font-serif text-2xl md:text-[2rem] leading-[1.2] text-ink/90">
              The thing we are most proud of at {COMPANY.name} is not the awards on the wall or the covers on the newsstand — it is how much we get to give. <span className="serif-italic">Ten percent of every client package. Every single one.</span> Being in a position to help others is the entire reason we built this house, and the only scoreboard we keep.
            </p>
          </Reveal>
          <Reveal delay={0.3} className="col-span-12 md:col-span-4 md:col-start-9">
            <p className="text-ink/75 leading-relaxed text-[17px]">
              When a client signs with us, they sign onto something larger than a campaign: <strong className="font-medium text-ink">eighteen organisations doing the real work in this world</strong>, and a standing invitation to follow them, donate to them, attend their events, and stand with them. Our cheque is the floor. What you build on top is the part worth celebrating.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ───── BIG STATS ───── */}
      <section className="container-edge mt-24 md:mt-32" data-testid="giveback-stats">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-ink/15 border-y border-ink/15">
          {[
            { n: "10%",  l: "Of every client package, given." },
            { n: "18",   l: "Partner organisations, hand-chosen." },
            { n: "♾️",   l: "Built into the company since day one." },
            { n: "100%", l: "Of donations route directly to the cause." },
          ].map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05} className="bg-cream px-3 py-7 sm:px-5 sm:py-8 md:px-6 md:py-10 text-center overflow-hidden">
              <div
                className="font-display leading-none whitespace-nowrap"
                style={{
                  fontFamily: /\p{Emoji}/u.test(s.n) ? "'Apple Color Emoji','Segoe UI Emoji','Noto Color Emoji',sans-serif" : undefined,
                  fontSize: "clamp(2.25rem, 10vw, 4.75rem)",
                  letterSpacing: "-0.04em",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {s.n}
              </div>
              <div className="text-ink/70 mt-3 font-serif italic text-[13px] sm:text-sm md:text-base max-w-[20ch] mx-auto leading-snug">{s.l}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ───── HONOR ROLL ───── */}
      <section className="container-edge mt-28 md:mt-36" data-testid="charity-grid">
        {/* Header */}
        <div className="grid grid-cols-12 gap-y-8 gap-x-8 items-end mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-7">
            <div className="overline text-ink/55">The Honor Roll</div>
            <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] mt-3">
              Causes the house <br/>
              <span className="serif-italic font-serif normal-case">stands behind</span>.
            </h2>
          </div>
          <p className="col-span-12 md:col-span-4 md:col-start-9 font-serif italic text-lg md:text-xl text-ink/75 leading-snug">
            Eighteen organisations doing far bigger work than ours — and the entire reason we go to work in the morning. To stand beside them is the legacy we are most proud of.
          </p>
        </div>

        {/* THEME CHIPS */}
        <div className="flex flex-wrap gap-2 md:gap-3 mb-12 md:mb-14" data-testid="theme-filter">
          {THEMES.map((t) => {
            const active = activeTheme === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTheme(t.key)}
                className={`group inline-flex items-center gap-2 px-4 py-2 border transition-all duration-300 overline ${
                  active
                    ? "bg-ink text-cream border-ink"
                    : "border-ink/25 text-ink hover:border-ink hover:bg-ink/5"
                }`}
                data-testid={`theme-chip-${t.key}`}
              >
                <span>{t.label}</span>
                <span className={`text-[10px] tabular-nums ${active ? "text-cream/70" : "text-ink/45"}`}>
                  {counts[t.key] || 0}
                </span>
              </button>
            );
          })}
        </div>

        {/* THE LIST — magazine-style honor roll */}
        <div className="border-b border-ink/15" data-testid="charity-grid-list">
          {filtered.map((c, i) => (
            <HonorRollEntry key={c.domain} c={c} n={i + 1} />
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="text-center font-serif italic text-ink/60 py-16">
            No partners in this category yet — that&apos;s on us.{" "}
            <button onClick={() => setActiveTheme("all")} className="underline">
              See all eighteen
            </button>.
          </p>
        )}
      </section>

      {/* ───── THE SCOREBOARD (moved to bottom — quiet close to the list) ───── */}
      <section className="container-edge mt-24" data-testid="giveback-pride">
        <div className="border-t border-b border-ink/15 py-12 md:py-16 text-center max-w-3xl mx-auto">
          <div className="overline text-ink/55 mb-4">The scoreboard we keep</div>
          <p className="font-serif text-2xl md:text-3xl leading-[1.15] text-ink">
            Awards age. Press cycles end. <br/>
            <span className="serif-italic">What we gave, and to whom, is the part that lasts.</span>
          </p>
        </div>
      </section>
    </div>
  );
}
